import { useCallback, useEffect, useRef } from 'react'
import { useDrag } from 'react-dnd'
import styles from './index.module.scss'
import Utils from '@/utils/utils'

const MULTIPLE_IMAGE_DRAG_TYPE = 'picture-viewer-multiple-image'

const statusMap = {
    approved: { label: '可用', className: 'approved' },
    rejected: { label: '需修改', className: 'rejected' },
    canceled: { label: '取消', className: 'canceled' },
    modified: { label: '已修改', className: 'modified' },
}

// 常见视频扩展名，用于判断 url 是否为视频资源
const VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i

/** 根据 url 扩展名判断是否为视频 */
function isVideoUrl(url) {
    if (!url) return false
    return VIDEO_EXTENSIONS.test(url)
}

export default function PicViewerImageList ({
    viewMode,
    images = [],
    currentIndex = 0,
    multipleSlots = {},
    total = 0,
    onSelect,
}) {
    const listRef = useRef(null)
    const itemRefs = useRef([])

    useEffect(() => {
        const el = itemRefs.current[currentIndex]
        if (el && listRef.current) {
            el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
    }, [currentIndex])

    const getActived = useCallback((index) => {
        if (viewMode == "multiple") return multipleSlots.map(i => i.imageIndex).includes(index)
        else return index === currentIndex
    }, [viewMode, multipleSlots, currentIndex])

    return (
        <div className={styles.PicViewerLeft}>
            <div className={styles.Progress}>
                第{currentIndex + 1}/{total}张
            </div>
            <div className={styles.ThumbnailList} ref={listRef}>
                {images.map((img, index) => {
                    const status = statusMap[img.status]
                    const statusLabel = img[`${img.status}Text`] || status?.label || ''
                    return (
                        <ThumbnailItem
                            key={index}
                            img={img}
                            index={index}
                            status={status}
                            statusLabel={statusLabel}
                            isActive={getActived(index)}
                            canDrag={viewMode === 'multiple'}
                            setItemRef={(el) => {
                                itemRefs.current[index] = el
                            }}
                            onSelect={onSelect}
                        />
                    )
                })}
            </div>
        </div>
    )
}

function ThumbnailItem({
    img,
    index,
    status,
    statusLabel,
    isActive,
    canDrag,
    setItemRef,
    onSelect,
}) {
    const [{ isDragging }, dragRef] = useDrag(() => ({
        type: MULTIPLE_IMAGE_DRAG_TYPE,
        canDrag,
        item: {
            imageIndex: index,
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }), [canDrag, index])

    return (
        <div
            ref={el => {
                setItemRef?.(el)
                if (canDrag) {
                    dragRef(el)
                }
            }}
            className={`${styles.ThumbnailItem} ${isActive ? styles.active : ''} ${isDragging ? styles.dragging : ''} ${canDrag ? styles.draggable : ''}`}
            onClick={() => onSelect?.(index)}
        >
            {
                isVideoUrl(img.url) ?
                <img src={Utils.getVideoSnapshot(img.url)} alt="" className={styles.ThumbnailImg} /> :
                <img src={Utils.getImgThumbnail(img.url)} alt="" className={styles.ThumbnailImg} />
            } {
                img.extraUrl && <img src={img.extraUrl} alt="" className={styles.ThumbnailExtraImg} />
            }
            {status && (
                <span className={`${styles.StatusBadge} ${styles[status.className]}`}>
                    {statusLabel}
                </span>
            )}
        </div>
    )
}

export { MULTIPLE_IMAGE_DRAG_TYPE }
